import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';

import cls from 'classnames/bind';

import getMaxDate from 'date-fns/max';
import startOfToday from 'date-fns/startOfToday';

import DatepickerHeader from '@ott/datepicker-header';
import PreventOutsideScroll from '@ott/prevent-outside-scroll';
import DM from '@ott/utility-date';

import Heatmap from '../../../Heatmap/Heatmap';
import { ANIMATION_TIME, WEEK_SCROLL_OFFSET } from '../../calendarConstants';
import Month from './blocks/Month/Month';

import scrollIntoView from 'scroll-into-view';

import s from './DatesContainer.scss';

const cx = cls.bind(s);

class DatesContainer extends PureComponent {
  static propTypes = {
    months: PropTypes.arrayOf(PropTypes.object),

    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    hoveredDate: PropTypes.instanceOf(Date),

    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    maxDays: PropTypes.number,

    scrollToWeek: PropTypes.bool,
    hideMonthsNavigation: PropTypes.bool,
    endOnlyAfterStart: PropTypes.bool,
    pickOneDate: PropTypes.bool,
    weekdays: PropTypes.array,
    heatmap: PropTypes.shape({
      currency: PropTypes.string.isRequired,
      min: PropTypes.number.isRequired,
      mid: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
      time: PropTypes.number.isRequired,
      prices: PropTypes.PropTypes.object.isRequired,
    }),
    heatmapStatus: PropTypes.shape({
      initial: PropTypes.bool.isRequired,
      loading: PropTypes.bool.isRequired,
      success: PropTypes.bool.isRequired,
      error: PropTypes.bool.isRequired,
      cancelled: PropTypes.bool.isRequired,
    }),
    withHeatmap: PropTypes.bool,
    showHeatmapLegend: PropTypes.bool,
    showHeatmapPrices: PropTypes.bool,
    isDirectOnly: PropTypes.bool,
    onDirectionSwitch: PropTypes.func,
    checkIfDateIsDisabled: PropTypes.func,

    onScroll: PropTypes.func,
    onDateSelect: PropTypes.func,
    onDateHover: PropTypes.func,
    onContainerLeave: PropTypes.func.isRequired,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.state = {
      isHovered: false,
    };
  }

  componentDidMount() {
    const { minDate, startDate, endDate, scrollToWeek } = this.props;

    const dateToScroll = startDate || getMaxDate([minDate, startOfToday()]);
    const isDateInDifferenceMonth =
      startDate && endDate && this.getMonthNode(startDate) !== this.getMonthNode(endDate);

    const node =
      isDateInDifferenceMonth || scrollToWeek
        ? this.getWeekNode(dateToScroll)
        : this.getMonthNode(dateToScroll);
    const scrollAreaDOMNode = findDOMNode(this.getScrollAreaRef());

    if (node) {
      scrollIntoView(findDOMNode(node), {
        time: ANIMATION_TIME,
        align: {
          top: 0,
          topOffset: WEEK_SCROLL_OFFSET,
        },
        validTarget: (target) => target === scrollAreaDOMNode,
        isScrollable: () => true,
      });
    }
  }

  getWeekNode(date) {
    const monthNode = this.getMonthNode(date);

    if (!monthNode) {
      return null;
    }

    return monthNode.getWeekNode(date);
  }

  getMonthNode(date) {
    return this[`month-${date.getMonth()}${date.getFullYear()}-ref`];
  }

  handleMouseEnter = () => {
    this.setState({
      isHovered: true,
    });
  };

  handleMouseLeave = () => {
    this.setState({
      isHovered: false,
    });
  };

  handleContainerMouseLeave = () => {
    this.props.onContainerLeave();
  };

  getHoveredDate(currentMonth, currentYear) {
    // todo перенести в sCU дочерних?
    const { hoveredDate, startDate, endDate, pickOneDate } = this.props;

    if (!hoveredDate || !startDate) {
      return;
    }

    if (pickOneDate) {
      if (currentYear === startDate.getFullYear() && currentMonth === startDate.getMonth()) {
        return hoveredDate;
      }
      return;
    }

    if (!endDate) {
      const monthBegin = new Date(currentYear, currentMonth);
      const monthEnd = DM.lastDayOfMonth(currentYear, currentMonth);

      if (DM.isOverlap(monthBegin, monthEnd, hoveredDate, startDate)) {
        return hoveredDate;
      }
    }
  }

  getScrollAreaRef = () => this._scrollAreaRef;

  setScrollAreaRef = (ref) => (this._scrollAreaRef = ref);

  renderHeatmap = () => {
    const {
      heatmap,
      heatmapStatus,
      withHeatmap,
      showHeatmapLegend,
      isDirectOnly,
      onDirectionSwitch,
      isMobile,
    } = this.props;

    if (!withHeatmap || isMobile) {
      return null;
    }

    return (
      <Heatmap
        heatmap={heatmap}
        heatmapStatus={heatmapStatus}
        isDirectOnly={isDirectOnly}
        onSwitch={onDirectionSwitch}
        showHeatmapLegend={showHeatmapLegend}
      />
    );
  };

  render() {
    const { isHovered } = this.state;

    const {
      months,
      onScroll,
      onDateHover,
      minDate,
      maxDate,
      onDateSelect,
      startDate,
      endDate,
      endOnlyAfterStart,
      pickOneDate,
      weekdays,
      maxDays,
      heatmap,
      heatmapStatus,
      withHeatmap,
      showHeatmapPrices,
      checkIfDateIsDisabled,
      hideMonthsNavigation,
      isMobile,
      showRouteDirections,
      routeDirectionsOptions,
      defaultOptionDirectionIndex,
      onOptionSelect,
    } = this.props;

    // плавный скролл в мобильном сафари
    // в iframe не работает. прыгает контент
    const isInIframe = typeof window !== 'undefined' && window !== window.parent;
    const withSmoothScroll = !isInIframe;

    return (
      <div
        className={cx('datesContainer', {
          'datesContainer--hideMonthsNavigation': hideMonthsNavigation,
          'datesContainer--isMobile': isMobile,
        })}
        onMouseLeave={this.handleContainerMouseLeave}
        data-locator="datesContainer"
        data-allow-touch-move={true}
      >
        {showRouteDirections && (
          <div className={cx('datepickerHeader')}>
            <DatepickerHeader
              options={routeDirectionsOptions}
              defaultOptionIndex={defaultOptionDirectionIndex}
              onOptionSelect={onOptionSelect}
            />
          </div>
        )}
        <div className={cx('weekdays')}>
          {weekdays.map((weekday, key) => (
            <div
              key={key}
              className={cx('weekday', {
                'weekday--isWeekend': weekday.isWeekend,
              })}
            >
              {weekday.label}
            </div>
          ))}
        </div>

        <PreventOutsideScroll ref={this.setScrollAreaRef}>
          <div
            className={cx('scrollArea', {
              'scrollArea--isNotHovered': !isHovered,
              'scrollArea--withSmoothScroll': withSmoothScroll,
            })}
            onScroll={onScroll}
            onMouseEnter={this.handleMouseEnter}
            onMouseLeave={this.handleMouseLeave}
          >
            <div
              className={cx('dates')}
              ref={(ref) => (this.datesRef = ref)}
              data-test="calendar-dates"
            >
              {months.map(({ idx, year, weeks, title }) => {
                return (
                  <Month
                    ref={(ref) => (this[`month-${idx}${year}-ref`] = ref)}
                    title={title}
                    startDate={startDate}
                    endDate={endDate}
                    heatmap={heatmap}
                    heatmapStatus={heatmapStatus}
                    withHeatmap={withHeatmap}
                    showHeatmapPrices={showHeatmapPrices}
                    endOnlyAfterStart={endOnlyAfterStart}
                    pickOneDate={pickOneDate}
                    hoveredDate={this.getHoveredDate(idx, year)}
                    weeks={weeks}
                    year={year}
                    hasCompactTitle={weeks[0][hideMonthsNavigation ? 2 : 1] === false}
                    minDate={minDate}
                    maxDate={maxDate}
                    maxDays={maxDays}
                    checkIfDateIsDisabled={checkIfDateIsDisabled}
                    key={`${idx}${year}`}
                    onDateHover={onDateHover}
                    onDateSelect={onDateSelect}
                  />
                );
              })}
            </div>
          </div>
        </PreventOutsideScroll>
        {this.renderHeatmap()}
      </div>
    );
  }
}

export default DatesContainer;
